<template>
  <header class="n2-cabecera" :class="{ 'n2-cabecera--scroll': conScroll }" v-if="NOTARIA?.Theme">
    <div v-if="T.DatosContacto?.MostrarStickyBar" class="n2-barra">
      <div class="n2-contenedor n2-barra__fila">
        <a v-if="T.DatosContacto.MostrarTelefono !== false && C.Telefono" :href="'tel:' + C.Telefono.replace(/\s/g, '')">
          <v-icon size="15">mdi-phone-outline</v-icon> {{ C.Telefono.trim() }}
        </a>
        <a v-if="T.DatosContacto.MostrarEmail !== false && C.Email" :href="'mailto:' + C.Email">
          <v-icon size="15">mdi-email-outline</v-icon> {{ C.Email }}
        </a>
        <a v-if="T.DatosContacto.MostrarEmail2 && C.Email2" :href="'mailto:' + C.Email2">
          <v-icon size="15">mdi-email-outline</v-icon> {{ C.Email2 }}
        </a>
        <span v-if="T.DatosContacto.MostrarDireccion !== false && C.Direccion" class="n2-barra__dir">
          <v-icon size="15">mdi-map-marker-outline</v-icon> {{ C.Direccion }}<template v-if="C.Municipio">, {{ C.Municipio }}</template>
        </span>
      </div>
    </div>

    <div class="n2-contenedor n2-cabecera__fila">
      <NuxtLink :to="getLocalePath('/')" class="n2-logo" :aria-label="NOTARIA.Nombre">
        <img v-if="T.Menu?.Logo" :src="imagenIDtoSRC(T.Menu.Logo)" :alt="'Logo ' + NOTARIA.Nombre" />
        <span v-else>{{ NOTARIA.Nombre }}</span>
      </NuxtLink>

      <nav class="n2-nav" :aria-label="$t('Inicio')">
        <template v-for="e in enlaces" :key="e.clave">
          <a v-if="e.externo" :href="e.href" target="_blank" rel="noopener">{{ e.texto }}</a>
          <NuxtLink v-else :to="e.to">{{ e.texto }}</NuxtLink>
        </template>
      </nav>

      <div class="n2-cabecera__acciones">
        <button v-if="NOTARIA.Personalizar?.Accesibilidad" type="button" class="n2-icono" @click="store.toggleModoAccesible"
          :title="$t(store.ModoAccesible ? 'DesactivarModoAccesibilidad' : 'ActivarModoAccesibilidad')"
          :aria-label="$t(store.ModoAccesible ? 'DesactivarModoAccesibilidad' : 'ActivarModoAccesibilidad')">
          <v-icon>mdi-human</v-icon>
        </button>

        <v-menu v-if="NOTARIA.Personalizar?.Multilenguaje && idiomas.length > 1">
          <template v-slot:activator="{ props }">
            <button type="button" class="n2-icono" v-bind="props" :aria-label="$t('seleccionIdioma')">
              <IconoIdioma :flag="store.getFlag" />
            </button>
          </template>
          <v-list density="compact">
            <v-list-item v-for="i in idiomas" :key="i.Language" @click="cambiarIdioma(i)" :title="i.Title">
              <template v-slot:prepend><IconoIdioma :flag="i.Flag" class="mr-2" /></template>
            </v-list-item>
          </v-list>
        </v-menu>

        <NuxtLink v-if="acceso.visible('menu')" :to="acceso.enlace('menu')" class="n2-boton n2-boton--acento n2-solo-escritorio">
          {{ (acceso.textoMenu.value || tSolicitud('Encargo online')) }}
        </NuxtLink>
        <a v-else-if="C.Telefono" :href="'tel:' + C.Telefono.replace(/\s/g, '')" class="n2-boton n2-boton--acento n2-solo-escritorio">
          <v-icon size="18">mdi-phone-outline</v-icon> {{ C.Telefono.trim() }}
        </a>

        <button type="button" class="n2-icono n2-solo-movil" :aria-expanded="abierto" aria-controls="n2-menu-movil"
          aria-label="Menú" @click="abierto = !abierto">
          <v-icon>{{ abierto ? 'mdi-close' : 'mdi-menu' }}</v-icon>
        </button>
      </div>
    </div>

    <div v-show="abierto" id="n2-menu-movil" class="n2-menu-movil" @click="abierto = false">
      <template v-for="e in enlaces" :key="'m' + e.clave">
        <a v-if="e.externo" :href="e.href" target="_blank" rel="noopener">{{ e.texto }}</a>
        <NuxtLink v-else :to="e.to">{{ e.texto }}</NuxtLink>
      </template>
      <NuxtLink v-if="acceso.visible('menu')" :to="acceso.enlace('menu')" class="n2-boton n2-boton--acento">
        {{ (acceso.textoMenu.value || tSolicitud('Encargo online')) }}
      </NuxtLink>
      <a v-if="C.Telefono" :href="'tel:' + C.Telefono.replace(/\s/g, '')" class="n2-boton n2-boton--linea">
        <v-icon size="18">mdi-phone-outline</v-icon> {{ C.Telefono.trim() }}
      </a>
    </div>
  </header>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { storeToRefs } from 'pinia'
import { useStore } from '../../store/index'
import { useUtils } from '#imports'
import { useAccesoSolicitud } from '../../composables/solicitudAcceso'
import { t as tSolicitud } from '../../composables/solicitudTextos'
import { useMenuNotaria } from '../../composables/useNotaria2'

const store = useStore()
const { NOTARIA } = storeToRefs(store)
const { imagenIDtoSRC, $t, getLocalePath } = useUtils()
const acceso = useAccesoSolicitud()
const route = useRoute()

const T = computed(() => NOTARIA.value?.Theme ?? {})
const C = computed(() => NOTARIA.value?.Contacto ?? {})
const abierto = ref(false)
const conScroll = ref(false)

const { enlaces, idiomas, cambiarIdioma } = useMenuNotaria()

watch(() => route.fullPath, () => { abierto.value = false })

function alHacerScroll() { conScroll.value = window.scrollY > 8 }
onMounted(() => { alHacerScroll(); window.addEventListener('scroll', alHacerScroll, { passive: true }) })
onBeforeUnmount(() => window.removeEventListener('scroll', alHacerScroll))

</script>
